// Bucket helpers for the Maturity & Coupon tracker.
//
// The buckets are defined by the dedicated tracker API: each carries a stable `id` and a
// portfolio `weight` (the percentage distribution shown by the bucket bar). The API returns a
// single flat bucket list shared by both sections; a bucket belongs to a section when at least
// one of that section's entries (bonds for maturity, coupons for coupon) references it by
// `bucketId`. Buckets keep the order in which the API returns them.
//
// Consumed both by the dashboard widget and by the report builder.

export type DisplayBucket = {
	/** Stable identifier — the API bucket id; safe to persist in report templates. */
	key: string;
	/** Full label with the unit spelled out, e.g. "0-1 years" or "6-12 months". */
	groupName: string;
	/** Compact label for the bucket bar — the raw API id, e.g. "0-1Y" or "10Y+". */
	shortName: string;
	/** Portfolio weight of the bucket, as a percentage (0-100). */
	weight: number;
};

/**
 * Spells out a bucket id's trailing unit letter for the full label: "Y" → year(s), "M" → month(s).
 * The digits/symbols around it are preserved, so "0-1Y" → "0-1 years", "10Y+" → "10+ years" and
 * "0-3M" → "0-3 months". Ids without a unit letter are returned unchanged.
 */
export function expandBucketUnit(id: string): string {
	const unitMatch = id.match(/[YM]/);
	if (!unitMatch) {
		return id;
	}
	const numericPart = id.replace(/[YM]/, "");
	const unit = unitMatch[0] === "Y" ? "year" : "month";
	// Singular only for an exact "1"; ranges like "0-1" stay plural.
	return `${numericPart} ${numericPart === "1" ? unit : `${unit}s`}`;
}

/**
 * Returns the buckets that belong to a section (the ones referenced by its entries), in API
 * order. A bucket referenced by no entry is dropped, so the maturity/coupon split falls out of
 * which entries point at each bucket.
 */
export function bucketsForSection(
	buckets: readonly { id: string; weight: number }[],
	entries: readonly { bucketId: string }[],
): DisplayBucket[] {
	const referenced = new Set(entries.map((entry) => entry.bucketId));
	return buckets
		.filter((bucket) => referenced.has(bucket.id))
		.map((bucket) => ({
			key: bucket.id,
			groupName: expandBucketUnit(bucket.id),
			shortName: bucket.id,
			weight: bucket.weight,
		}));
}

/** Sum of the amounts that are present, to the cent; null when none is (every row N/A). */
export function sumAmounts(amounts: readonly (number | null | undefined)[]): number | null {
	const present = amounts.filter((amount): amount is number => amount != null);
	if (present.length === 0) {
		return null;
	}
	return Math.round(present.reduce((sum, amount) => sum + amount, 0) * 100) / 100;
}

/**
 * Groups a section's entries into its display buckets, mapping each entry to a display row.
 * Buckets keep their API order, carry their weight, and empty buckets are dropped.
 * `amountOf` reads a row's € amount; each group then carries the `total` of its present amounts.
 */
export function groupBucketRows<TEntry extends { bucketId: string }, TRow>(
	buckets: readonly { id: string; weight: number }[],
	entries: readonly TEntry[],
	toRow: (entry: TEntry) => TRow,
	amountOf: (row: TRow) => number | null | undefined,
): {
	key: string;
	groupName: string;
	shortName: string;
	weight: number;
	total: number | null;
	rows: TRow[];
}[] {
	return bucketsForSection(buckets, entries)
		.map(({ key, groupName, shortName, weight }) => {
			const rows = entries.filter((entry) => entry.bucketId === key).map(toRow);
			return {
				key,
				groupName,
				shortName,
				weight,
				total: sumAmounts(rows.map(amountOf)),
				rows,
			};
		})
		.filter((group) => group.rows.length > 0);
}
